/**
* @summary - Asset SubForm Grid
* @author Yuvasri S
* @since 2024-12-19
*/
import { Box, Button, Grid, Modal, Stack, Typography } from '@mui/material';
import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react'
import AddIcon from '@mui/icons-material/Add';
import { useDispatch } from 'react-redux';
import CustomTextFieldWithSearch from '../commoncomponents/CustomTextFieldWithSearch';
import CustomTextField from '../commoncomponents/CustomTextField';
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import { DefaultCard } from '../../assets/styled-components/card';
import LicenseCertificateRepository from '../../api/repositories/licenseCertificateRepository';
import CustomDialog from '../commoncomponents/CustomDialog';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

const AddRowDialogue = ({ open, onClose, rows, setRows, editMode, setErrorState, setRowLength, service }) => {
    const initialState = {
        assetNo: "",
        assetDesc: "",
    };

    const dispatch = useDispatch();
    const [subFormData, setSubFormData] = useState(initialState);
    const [assetNoOptions, setAssetNoOptions] = useState([]);
    const [error, setError] = useState(""); // Error state

    const handleOnchange = (value) => {
        setSubFormData((prev) => ({
            ...prev,
            assetNo: value?.assetNoDesc || "",
            assetDesc: value?.assetDesc || "",
        }));
        setError(""); // Clear error when user changes the value
    };

    const getAssetNo = async () => {
        try {
            const response = await LicenseCertificateRepository.fetchAssetNo(service);
            setAssetNoOptions(response);
        } catch (error) {
            console.error("Failed to fetch asset numbers:", error);
            setAssetNoOptions([]); // Optional: Reset options on error
        }
    };

    useEffect(() => {
        getAssetNo();
    }, [service]);

    const handleCancel = () => {
        onClose();
        setSubFormData(initialState);
        setError("");
    };

    const handleSave = (e) => {
        e.preventDefault();

        const duplicate = rows.some(
            (row) => row.assetNo.id === subFormData.assetNo.id
        );

        if (duplicate) {
            setError("Duplicate asset number detected. Please select a unique asset.");
            return;
        }

        const newItem = {
            ...subFormData,
            dummy_id: uuidv4(),
        };
        setRowLength(rows.length + 1);
        setRows((prevRows) => [...prevRows, newItem]);
        setErrorState("");
        setSubFormData(initialState);
        onClose();
    };

    const isAddDisabled = !subFormData?.assetNo?.label;

    return (
        <Modal
            open={open}
            onClose={handleCancel}
            aria-labelledby="add-asset-title"
            aria-describedby="add-asset-description"
        >
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 500,
                    bgcolor: "background.paper",
                    boxShadow: 24,
                    p: 4,
                    outline: "none",
                    borderRadius: "10px",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography id="add-asset-title" variant="title2" component="h2" gutterBottom>
                        Add Asset
                    </Typography>
                </Box>
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                        <CustomTextFieldWithSearch
                            autoFocus
                            label="Asset No"
                            id="assetNo"
                            name="assetNo"
                            value={subFormData?.assetNo || ""}
                            options={assetNoOptions || []}
                            onChange={handleOnchange}
                            isMandatoryField
                            error={!!error}
                            errorMessage={error}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <CustomTextField
                            label="Asset Description"
                            id="assetDesc"
                            name="assetDesc"
                            value={subFormData?.assetDesc || ""}
                            disabled
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{ mr: 2 }}
                        disabled={isAddDisabled}
                    >
                        {editMode ? "UPDATE" : "ADD"}
                    </Button>
                    <Button variant="outlined" onClick={handleCancel}>
                        CANCEL
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};



const AssetSubformGrid = forwardRef((props, ref) => {
    const location = useLocation();
    const editMode = location?.state?.editmode;
    const viewMode = location?.state?.viewmode;
    const [gridAPI, setGridAPI] = useState({});
    const [rows, setRows] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [error, setError] = useState('')

    const [subFormDeleteID, setSubFormDeleteId] = useState({});
    const [subFormDeleteDialog, setSubFormDeleteDialog] = useState(false);

    function AddRowButton({ onClick }) {
        return (
            <Box sx={{ mt: 2 }}>
                <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={onClick}>
                    Add Row
                </Button>
            </Box>
        );
    };

    const handleOpenDialog = () => {
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        // setEditData(null);
        setDialogOpen(false);
    };


    const handleConfirm = () => {
        handleCloseDialog();
    };

    const columns_Field = [
        {
            field: 'assetNo',
            headerName: 'Asset No',
            flex: 1,
            valueGetter: (params) => params?.data?.assetNo ? params?.data.assetNo?.label : '',
        },
        {
            field: 'assetDesc',
            headerName: 'Asset Description',
            flex: 1
        },

    ];

    const handleDelete = (rowData) => {
        setSubFormDeleteDialog(true);
        setSubFormDeleteId(rowData);
    };

    const handleCloseSubFormDeleteDialog = () => {
        setSubFormDeleteDialog(false);
    };

    const handleConfirmSubFormDelete = async () => {
        try {
            let updatedRows;

            if (subFormDeleteID?.id) {
                // Delete record from API if it has an ID
                await LicenseCertificateRepository?.deleteSubForm(subFormDeleteID?.id);

                updatedRows = rows?.filter((row) => row?.id !== subFormDeleteID?.id);
            } else if (subFormDeleteID?.dummy_id) {
                // Remove locally if it's a new (unsaved) record
                updatedRows = rows?.filter((row) => row?.dummy_id !== subFormDeleteID?.dummy_id);
            }

            setRows(updatedRows);

            // Update rowLength after deletion
            const newRowLength = updatedRows.length;
            props.setRowLength(newRowLength);

            // Display an error if the grid is empty
            if (newRowLength < 1) {
                setError("You must have at least one row in the grid.");
            } else {
                setError(""); // Clear error if rows are valid
            }

            setSubFormDeleteDialog(false);
        } catch (error) {
            console.error("Error during deletion:", error);
        }
    };




    const handleSubFormSubmit = async (headerId) => {
        // Validation: Check if rows are empty or below a certain number
        if (!rows || rows.length <= 0) {
            setError("Please add at least one row before submitting.");
            return;
        }

        if (rows.length < 1) { // Example threshold (adjust as needed)
            setError("Number of rows must be at least 1.");
            return;
        }

        setError(""); // Clear error if rows are valid
        const subFormPayload = rows?.map((row) => ({
            maslicensecertheaderId: headerId,
            licnresourceid: row.assetNo.id || "",
            licnresourcedesc: row?.assetDesc || "",
            id: editMode ? row?.id : ""
        }));

        try {
            const createSubFormRes = await LicenseCertificateRepository.createOrUpdateSubForm(headerId, subFormPayload)

            return createSubFormRes;
        } catch (error) {
            console.error('Error while submitting subform:', error);
        }
    };


    useImperativeHandle(ref, () => ({
        handleSubFormSubmit,
    }));

    const getRows = async () => {
        try {
            const listRows = await LicenseCertificateRepository.getSubForm(props.service, location?.state?.id);

            const mappedRows = Array.isArray(listRows)
                ? listRows.map(row => ({
                    assetNo: {
                        id: row?.licnresourceid || "",
                        label: row?.name || "",
                    },
                    assetDesc: row?.licnresourcedesc || "",
                    id: row?.id || "",
                }))
                : [];

            setRows(mappedRows);

            // Update rowLength with the number of retrieved rows
            props.setRowLength(mappedRows.length);

            // Validation: Check if rows are below the threshold
            if (mappedRows.length < 1) {
                viewMode ? setError("") : setError("Please add at least one row.");
            } else {
                setError(""); // Clear error if valid
            }
        } catch (error) {
            console.error("Error fetching rows:", error);
            // Ensure rowLength is reset if an error occurs
            setRows([]);
            props.setRowLength(0);
            setError("Failed to fetch rows. Please try again.");
        }
    };


    useEffect(() => {
        getRows();
    }, [])

    return (
        <>
            <DefaultCard>
                <Box mt={{ xs: 1, md: 2 }} sx={{ marginTop: '0rem' }}>
                    {error && (
                        <Typography component={"span"} color={"error"}>
                            {error}*
                        </Typography>
                    )}

                    <Box mb={2} />

                    <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" mb={2}>
                        <Typography variant="h6" gutterBottom sx={{ marginTop: '0.5rem' }}>
                            Asset Details List
                        </Typography>
                        <Box sx={{ flexGrow: 1 }} />
                        {!viewMode && (
                            <AddRowButton onClick={handleOpenDialog} />
                        )}
                    </Stack>


                    <CustomAgGrid
                        refSet={gridAPI}
                        rows={rows}
                        columns={columns_Field}
                        setGridApi={setGridAPI}
                        {...(!viewMode && {
                            handleDelete: (rowData) => handleDelete(rowData),
                        })}
                    />

                </Box>

                <AddRowDialogue
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    onConfirm={handleConfirm}
                    rows={rows}
                    setRows={setRows}
                    setErrorState={setError}
                    setRowLength={props.setRowLength}
                    service={editMode ? location?.state?.service : props.service}
                />
                <CustomDialog
                    open={subFormDeleteDialog}
                    onClose={handleCloseSubFormDeleteDialog}
                    onConfirm={handleConfirmSubFormDelete}
                    content="Are you sure you want to Delete?"
                    cancelBtn="No"
                    confirmBtn="Yes"
                />

            </DefaultCard>
        </>
    )
})

export default AssetSubformGrid;


